import React, { useCallback, useEffect, useState } from 'react';
import TextField, { StandardTextFieldProps } from '@mui/material/TextField';
import { AnySchema } from 'yup';
import { useFieldValidation } from '../../../hooks/formValidate';

export interface InputWithValidateProps<T = { [key: string]: unknown }> {
  onValidation?: (key: string, isValid: boolean, schema?: AnySchema) => void;
  onValue?: (key: keyof T & string, value: any | null) => void;
  onRemoveValue?: (key: string) => void;
  hideValidationError?: boolean;
}

export type TextInputWithValidateProps<T = { [key: string]: unknown }> = StandardTextFieldProps &
  InputWithValidateProps<T> & {
    validation?: AnySchema;
    value?: string | number;
    name: keyof T & string;
  };

function TextInputWithValidate<T>({
  value,
  name,
  onValue,
  onValidation,
  validation,
  variant,
  fullWidth,
  helperText,
  hideValidationError,
  ...props
}: TextInputWithValidateProps<T>) {
  const { doValidate, errorMessage } = useFieldValidation();
  const [controlledValue, setControlledValue] = useState<string | number>(value ?? '');

  useEffect(() => {
    setControlledValue(value ?? '');
  }, [value]);

  useEffect(() => {
    const validate = async () => {
      if (validation) {
        const isValid = await doValidate(controlledValue, validation);
        onValidation?.(name, isValid, validation);
      }
    };
    validate();
  }, [controlledValue, doValidate, name, onValidation, validation]);

  const onChangeEvent = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      setControlledValue(value);
      if (onValue) {
        onValue(name as keyof T & string, value);
      }
    },
    [onValue]
  );

  return (
    <TextField
      name={name}
      value={controlledValue}
      onChange={onChangeEvent}
      error={!hideValidationError && !!errorMessage}
      helperText={(!hideValidationError && errorMessage) || helperText}
      variant={variant || 'outlined'}
      fullWidth={fullWidth || true}
      {...props}
    />
  );
}

export default TextInputWithValidate;
